import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './BreakScheduleModal.module.css'
import o from '../../components/Overlay.module.css'
import ov from './overlays.module.css'
import { apiJson } from '../../lib/api'
import { MONS } from './time'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, useSlow, inertClass } from '../../components/pending'
import { DateField } from '../../components/DateField'

// Break schedule (CONTROL V4 openSched / saveSched / delSchedVersion, handoff
// §7f "The break schedule opens on a region default"; GET/POST
// /api/break-schedule). Editing starts from the rules in force on the day
// being viewed. The default toggle is derived from those rules, never assumed,
// and while it is on the table is read-only. The region default, its label and
// its basis note all come from the tenant's region.

export interface BreakRule {
  kind: 'paid' | 'unpaid'
  mins: number
  afterH: number
  over: boolean
}
interface Version {
  id: string | null
  effective_from: string | null
  rules: BreakRule[]
  source: 'version' | 'region'
}
export interface Schedule {
  region: { code: string; label: string; rules: BreakRule[]; basis_note: string }
  versions: Version[]
  in_force: { version_id: string | null; effective_from: string | null; rules: BreakRule[]; is_default: boolean }
}
interface DraftRule {
  kind: 'paid' | 'unpaid'
  mins: string
  afterH: string
  over: boolean
}
type Scope = 'forward' | 'all'

// V4 fmtDate: the base version has no date and reads as the start of records.
const SHORT_MONS = MONS.map((m) => m.slice(0, 3))
export function fmtDate(iso: string | null): string {
  if (!iso) return 'the start of records'
  const [y, m, d] = iso.split('-').map(Number)
  return `${d} ${SHORT_MONS[m - 1]} ${y}`
}
const summary = (rules: BreakRule[]) =>
  rules.length ? rules.map((r) => `${r.mins}m ${r.kind} ${r.over ? '>' : '≥'}${r.afterH}h`).join(' · ') : 'No breaks'
const toDraft = (rules: BreakRule[]): DraftRule[] => rules.map((r) => ({ kind: r.kind, mins: String(r.mins), afterH: String(r.afterH), over: r.over }))
// V4 saveSched: a row with no minutes or no threshold is dropped, the rest sorted.
export const draftToRules = (d: DraftRule[]): BreakRule[] =>
  d
    .filter((r) => (parseInt(r.mins, 10) || 0) > 0 && !Number.isNaN(parseFloat(r.afterH)))
    .map((r) => ({ kind: r.kind, mins: parseInt(r.mins, 10), afterH: parseFloat(r.afterH), over: !!r.over }))
    .sort((a, b) => a.afterH - b.afterH)
const lockedNote = (dates: string[]) =>
  dates.length ? ` · Wages not recalculated for locked ${dates.length === 1 ? 'day' : 'days'}: ${dates.map(fmtDate).join(', ')}` : ''

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
)

interface Props {
  user: any
  date: string
  onClose: () => void
  // A save closes the dialog; removing a version keeps it open. Either way the
  // day behind it is re-read, because its computed breaks may have moved.
  onSaved: (flash: string) => void
  onChanged: (flash: string) => void
}

export function BreakScheduleModal({ user, date, onClose, onSaved, onChanged }: Props) {
  const [sched, setSched] = useState<Schedule | null>(null)
  const [draft, setDraft] = useState<DraftRule[]>([])
  const [defaultOn, setDefaultOn] = useState(false)
  const [scope, setScope] = useState<Scope>('forward')
  const [from, setFrom] = useState(date)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The draft is what the schedule in force read into; the start for "changed".
  const [loaded, setLoaded] = useState(false)
  const userRef = useRef(user)
  userRef.current = user

  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once the draft
  // has moved from the schedule it opened on. Nothing closes it while its
  // write is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ draft, defaultOn, scope, from }, loaded)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: loaded, busy })

  const read = useCallback(async () => {
    const s = await apiJson<Schedule>(userRef.current, `/api/break-schedule?date=${date}`)
    setSched(s)
    return s
  }, [date])

  useEffect(() => {
    read()
      .then((s) => {
        setDraft(toDraft(s.in_force.rules))
        setDefaultOn(s.in_force.is_default)
        setLoaded(true)
      })
      .catch((e) => setError(e?.message || 'Could not load the break schedule'))
  }, [read])

  const locked = defaultOn
  const retro = scope === 'all'

  const setRule = (i: number, k: keyof DraftRule, v: string) =>
    setDraft((d) =>
      d.map((r, j) => {
        if (j !== i) return r
        if (k === 'kind') return { ...r, kind: v as DraftRule['kind'] }
        if (k === 'over') return { ...r, over: v === 'over' }
        return { ...r, [k]: String(v).replace(/[^0-9.]/g, '').slice(0, 4) }
      })
    )

  const toggleDefault = () => {
    if (!sched) return
    if (defaultOn) setDefaultOn(false)
    else {
      setDefaultOn(true)
      setDraft(toDraft(sched.region.rules))
    }
  }

  async function save() {
    if (!sched || busy) return
    setBusy(true)
    setError(null)
    try {
      const rules = locked ? sched.region.rules : draftToRules(draft)
      const r = await apiJson<{ locked_dates: string[] }>(user, '/api/break-schedule', {
        body: retro ? { scope: 'all', rules } : { scope: 'forward', effective_from: from || date, rules },
      })
      const head = retro ? 'Break schedule applied to every day, past included' : `Break schedule applies from ${fmtDate(from || date)} onwards`
      onSaved(head + lockedNote(r.locked_dates || []))
    } catch (e: any) {
      setError(e?.message || 'Could not save the break schedule')
      setBusy(false)
    }
  }

  async function removeVersion(v: Version) {
    if (!v.id || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<{ locked_dates: string[] }>(user, `/api/break-schedule/versions/${v.id}`, { method: 'DELETE' })
      await read()
      onChanged(`Break schedule effective ${fmtDate(v.effective_from)} removed` + lockedNote(r.locked_dates || []))
    } catch (e: any) {
      setError(e?.message || 'Could not remove this version')
    } finally {
      setBusy(false)
    }
  }

  const inForceFrom = sched?.in_force.effective_from ?? null
  const inForceId = sched?.in_force.version_id ?? null
  const region = sched?.region.label || ''

  return (
    <>
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${ov.split} ${ov.schedModal}`} role="dialog" aria-label="Break schedule">
        <div className={ov.splitHead}>
          <div className={o.headText}>
            <h2 className={o.title}>Break schedule</h2>
            <p className={o.sub}>
              What each worker is granted once their recorded time for the day meets a threshold. The schedule runs across the whole working day, not per
              session, and paid breaks stay inside recorded time while unpaid breaks are reconciled against any gap.
            </p>
          </div>
        </div>

        <div className={`${styles.body} ${inertClass(busy, slow)}`}>
          {sched && (
            <>
              <div className={`${o.toggleRow} ${styles.defaultBox}`}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>Use the {region} statutory default</div>
                  <div className={o.toggleSub}>
                    {locked
                      ? `The rules below are the ones Dataphyll ships for ${region}, the region set for this subscription. A subscription set to Australia gets a different set.`
                      : `You are running your own rules. Switching back on replaces them with the ${region} default.`}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={defaultOn}
                  aria-label={`Use the ${region} statutory default`}
                  className={o.switch}
                  onClick={toggleDefault}
                />
              </div>

              <div className={styles.gridHead}>
                <span className={styles.colHead}>Kind</span>
                <span className={styles.colHeadMid}>Minutes</span>
                <span />
                <span className={styles.colHeadMid}>Trigger</span>
                <span className={styles.colHeadMid}>Hours</span>
                <span />
              </div>
              {draft.map((r, i) => (
                <div key={i} className={`${styles.rule} ${locked ? styles.ruleLocked : ''}`} data-testid="break-rule">
                  <select
                    aria-label={`Rule ${i + 1} kind`}
                    className={`${styles.select} ${r.kind === 'unpaid' ? styles.kindUnpaid : styles.kindPaid}`}
                    value={r.kind}
                    disabled={locked}
                    onChange={(e) => setRule(i, 'kind', e.target.value)}
                  >
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                  <input
                    aria-label={`Rule ${i + 1} minutes`}
                    className={styles.field}
                    inputMode="numeric"
                    value={r.mins}
                    disabled={locked}
                    onChange={(e) => setRule(i, 'mins', e.target.value)}
                  />
                  <span className={styles.ruleWords}>minutes once the day</span>
                  <select
                    aria-label={`Rule ${i + 1} trigger`}
                    className={styles.select}
                    value={r.over ? 'over' : 'atleast'}
                    disabled={locked}
                    onChange={(e) => setRule(i, 'over', e.target.value)}
                  >
                    <option value="atleast">reaches</option>
                    <option value="over">passes</option>
                  </select>
                  <input
                    aria-label={`Rule ${i + 1} hours`}
                    className={styles.field}
                    inputMode="numeric"
                    value={r.afterH}
                    disabled={locked}
                    onChange={(e) => setRule(i, 'afterH', e.target.value)}
                  />
                  {!locked ? (
                    <button type="button" className={styles.iconBtn} title="Remove this rule" onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}>
                      <TrashIcon />
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              ))}
              {!locked && (
                <button
                  type="button"
                  className={styles.addRule}
                  onClick={() => setDraft((d) => d.concat([{ kind: 'paid', mins: '10', afterH: '', over: true }]))}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add a rule
                </button>
              )}

              <div className={`${o.warn} ${styles.warn}`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
                </svg>
                <div>
                  Rest and meal break entitlements are set by law, and the law differs by country, state and often by employment agreement. Dataphyll will
                  apply whatever schedule you enter. It does not check it against any jurisdiction. Making sure this schedule meets your obligations is your
                  responsibility, so take advice if you are not certain.
                </div>
              </div>

              <div className={styles.note}>
                <strong>Reaches</strong> counts the hour itself, <strong>passes</strong> does not. It only matters on a day that lands exactly on a
                threshold: a 4-hour shift <em>reaches</em> 4 hours but does not <em>pass</em> it, so with the statutory defaults it gets the rest break and no
                meal break. Everything longer or shorter is unaffected.
              </div>

              <div className={styles.section}>
                <div className={styles.sectionHead}>When does this apply?</div>
                <div className={styles.scopes}>
                  {(
                    [
                      ['forward', 'From a date onwards', 'Earlier days keep the rules they were worked under.'],
                      [
                        'all',
                        'Every day, past included',
                        'Rewrites how every day is read and removes any other dated version. Days already sent to payroll will no longer match what was paid.',
                      ],
                    ] as const
                  ).map(([k, label, note]) => (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={scope === k}
                      className={`${styles.scope} ${scope === k ? (k === 'all' ? styles.scopeRetro : styles.scopeOn) : ''}`}
                      onClick={() => setScope(k)}
                    >
                      <span className={styles.scopeTitle}>{label}</span>
                      <span className={styles.scopeNote}>{note}</span>
                    </button>
                  ))}
                </div>
                {!retro && (
                  <div className={styles.fromRow}>
                    <label htmlFor="sched-from">Effective from</label>
                    <DateField id="sched-from" className={styles.dateField} value={from} onChange={(e) => setFrom(e.target.value)} />
                  </div>
                )}
              </div>

              <div className={styles.section}>
                <div className={styles.sectionHead}>Schedule history</div>
                <div className={styles.inForce}>
                  In force on {fmtDate(date)}: the version effective {fmtDate(inForceFrom)}
                </div>
                <div className={styles.versions}>
                  {sched.versions.map((v) => (
                    <div key={v.id || 'region'} className={styles.version} data-testid="break-version">
                      <div className={styles.versionText}>
                        <div className={styles.versionHead}>
                          <span className={styles.versionLabel}>Effective {fmtDate(v.effective_from)}</span>
                          {v.id === inForceId && v.effective_from === inForceFrom && <span className={styles.inForcePill}>In force</span>}
                        </div>
                        <div className={styles.versionSummary}>{summary(v.rules)}</div>
                      </div>
                      {v.id && v.effective_from && (
                        <button type="button" className={styles.iconBtn} title="Remove this version" disabled={busy} onClick={() => removeVersion(v)}>
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.note} style={{ marginTop: 16 }}>
                {sched.region.basis_note}
              </div>
              <div className={styles.note} style={{ marginTop: 10 }}>
                Each day is read against the schedule in force on that day. Shifts with a manual break override keep their entered minutes.
              </div>
            </>
          )}
          {error && (
            <div className={`${o.danger} ${styles.error}`} role="alert">
              {error}
            </div>
          )}
        </div>

        <div className={ov.splitFoot}>
          {!locked && sched && (
            <button type="button" className={o.ghost} onClick={() => setDraft(toDraft(sched.region.rules))} disabled={busy}>
              Restore defaults
            </button>
          )}
          <button type="button" className={`${o.ghost} ${ov.pushRight}`} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <PendingButton type="button" className={retro ? `${o.dangerBtn} ${ov.noAuto}` : `${o.primary} ${ov.noAuto}`} onClick={save} disabled={!sched || (!retro && !from)} busy={busy} pending={retro ? 'Applying…' : 'Applying…'}>
            {retro ? 'Apply to every day, past included' : `Apply from ${fmtDate(from || date)}`}
          </PendingButton>
        </div>
      </div>
    </div>
    {confirm}
    </>
  )
}
